import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useFormOptions } from 'features/ai-blog-writer/hooks/useFormOptions';
import { Loader, Minus, Plus } from 'lucide-react';
import { types, tones } from 'features/email-generator/constants';
import React, { useState } from 'react';
import { useEmailContext } from 'features/email-generator/EmailGeneratorContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { emailGenApi } from 'features/email-generator/services/EmailGenApi';

const InputFormMobile = () => {
  const navigate = useNavigate();
  const {
    updateEmailContent,
    emailContent,
    isGenerating,
    setIsGenerating,
    formState,
    setFormState,
  } = useEmailContext();

  // Add state for custom inputs
  const [customType, setCustomType] = useState('');
  const [customTone, setCustomTone] = useState('');
  const [showCustomTypeInput, setShowCustomTypeInput] = useState(false);
  const [showCustomToneInput, setShowCustomToneInput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate input fields
    if (formState) {
      // Create a copy of the form state
      const updatedFormState = { ...formState };
      
      // If custom type is selected, use the custom value
      if (showCustomTypeInput && customType.trim()) {
        updatedFormState.type = customType.trim();
      }
      
      // If custom tone is selected, use the custom value
      if (showCustomToneInput && customTone.trim()) {
        updatedFormState.tone = customTone.trim();
      }
      
      if (
        !updatedFormState.type ||
        !updatedFormState.tone ||
        !updatedFormState.prompt ||
        !updatedFormState.language
      ) {
        toast.error('Please fill in all required fields.');
        return;
      }
      // API call
      try {
        setIsGenerating(true);
        const emailPromises = [];
        // Make API calls based on the number of drafts
        for (let i = 0; i < updatedFormState.drafts; i++) {
          emailPromises.push(
            emailGenApi.generateEmail({
              type: updatedFormState.type,
              tone: updatedFormState.tone,
              prompt: updatedFormState.prompt,
              language: updatedFormState.language,
            })
          );
        }

        if (emailPromises.length > 0) {
          let emailResponses: string[] = [];
          try {
            emailResponses = await Promise.all(emailPromises);
          } catch (error) {
            console.error('Error generating emails:', error);
          }

          if (emailResponses) {
            updateEmailContent(emailResponses);
          }
        }

        console.log(emailContent);
        navigate('/email-generator/email');
      } catch (err: any) {
        console.error('Error generating email:', err);
      } finally {
        setIsGenerating(false);
      }
    }
  };
  const { data } = useFormOptions();
  return (
    <form className="px-4 bg-background text-text-primary">
      <div className="flex flex-col gap-[6px] mt-2">
        <label className="text-text-primary  text-[14px] leading-4 font-medium">
          Email topic
        </label>
        <textarea
          className="resize-none rounded-[6px] bg-background border-[#00180821] h-20 placeholder:text-[#00110b78] focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] placeholder:font-normal placeholder:text-[16px] focus:border-[#846CEC]"
          placeholder="Enter email topic here"
          value={formState && formState.prompt}
          onChange={(e) =>
            setFormState({ ...formState, prompt: e.target.value })
          }
        />
      </div>
      <div className="grid grid-rows-2 grid-cols-2 mt-5 gap-y-5 gap-x-5">
        <div className="flex flex-col gap-[6px]">
          <label className=" text-[14px] leading-4 font-medium">Type</label>
          <Select
            value={formState?.type}
            onValueChange={(value) => {
              if (value === 'custom') {
                setShowCustomTypeInput(true);
                setFormState({ ...formState, type: 'custom' });
              } else {
                setShowCustomTypeInput(false);
                setFormState({ ...formState, type: value });
              }
            }}
          >
            <SelectTrigger className="touch-manipulation">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="z-[9999]"
            >
              {types.map((item: string, index: number) => {
                return (
                  <SelectItem
                    key={index}
                    value={item}
                    className="touch-manipulation"
                  >
                    {item}
                  </SelectItem>
                );
              })}
              <SelectItem value="custom" className="touch-manipulation">
                Custom (enter your own)
              </SelectItem>
            </SelectContent>
          </Select>
          {showCustomTypeInput && (
            <div className="mt-2">
              <Input
                placeholder="Enter custom email type"
                value={customType}
                onChange={(e) => {
                  setCustomType(e.target.value);
                  setFormState({ ...formState, type: e.target.value });
                }}
                className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] touch-manipulation"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[6px]">
          <label className="text-[14px] leading-4 font-medium">Tone</label>
          <Select
            value={formState?.tone}
            onValueChange={(value) => {
              if (value === 'custom') {
                setShowCustomToneInput(true);
                setFormState({ ...formState, tone: 'custom' });
              } else {
                setShowCustomToneInput(false);
                setFormState({ ...formState, tone: value });
              }
            }}
          >
            <SelectTrigger className="touch-manipulation">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="z-[9999]"
            >
              {tones.map((item: string, index: number) => (
                <SelectItem
                  key={index}
                  value={item}
                  className="touch-manipulation"
                >
                  {item}
                </SelectItem>
              ))}
              <SelectItem value="custom" className="touch-manipulation">
                Custom (enter your own)
              </SelectItem>
            </SelectContent>
          </Select>
          {showCustomToneInput && (
            <div className="mt-2">
              <Input
                placeholder="Enter custom tone"
                value={customTone}
                onChange={(e) => {
                  setCustomTone(e.target.value);
                  setFormState({ ...formState, tone: e.target.value });
                }}
                className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] touch-manipulation"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[6px]">
          <label className=" text-[14px] leading-4 font-medium">Language</label>
          <Select
            value={formState?.language}
            onValueChange={(value) =>
              setFormState({ ...formState, language: value })
            }
          >
            <SelectTrigger className="touch-manipulation">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="z-[9999]"
            >
              {data?.languages.map((item: { name: string }, index: number) => (
                <SelectItem
                  key={index}
                  value={item?.name}
                  className="touch-manipulation"
                >
                  {item?.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-[6px]">
          <label className="text-text-primary text-[14px] leading-4 font-medium">
            No. of Drafts
          </label>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-[100px]">
              <Input
                type="number"
                min="1"
                className="focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] placeholder:font-normal text-[17px] focus:border-[#846CEC] text-center font-semibold h-10 touch-manipulation bg-background"
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value > 0) {
                    setFormState({ ...formState, drafts: value });
                  }
                }}
                value={formState && formState.drafts}
              />
            </div>
            <Button
              className="h-10 w-10 rounded-md bg-[#4400EE]/15 flex items-center justify-center text-[#846CEC] hover:bg-[#846CEC] hover:text-white touch-manipulation"
              onClick={() =>
                setFormState({ ...formState, drafts: formState.drafts + 1 })
              }
              type="button"
              aria-label="Increase drafts"
            >
              <Plus size={16} />
            </Button>
            <Button
              className="h-10 w-10 rounded-md bg-[#4400EE]/15 flex items-center justify-center text-[#846CEC] hover:bg-[#846CEC] hover:text-white touch-manipulation"
              onClick={() =>
                setFormState({
                  ...formState,
                  drafts: Math.max(1, formState.drafts - 1),
                })
              }
              type="button"
              aria-label="Decrease drafts"
            >
              <Minus size={16} />
            </Button>
          </div>
        </div>
      </div>
      <Button
        className="w-full mt-7"
        variant="customPrimary"
        disabled={
          !formState?.drafts ||
          !formState.language ||
          !formState.prompt ||
          !formState.tone ||
          !formState.type ||
          isGenerating
        }
        onClick={handleSubmit}
      >
        {isGenerating && <Loader className="animate-spin" />}
        <span>Generate</span>
      </Button>
    </form>
  );
};

export default InputFormMobile;
